import {
  IAspiJSON,
  IMerchantJSON,
  IOutletFiles,
  IOutletPoP,
} from "src/interfaces";
import {
  JSONTry,
  maskBankAccountName,
  maskBankAccountNumber,
  safeParse,
} from "../helpers";
import { computed, observable } from "mobx";

import moment from "moment";

export type ProductName =
  | "GORESTO"
  | "GOPAY"
  | "POS"
  | "MIDTRANS"
  | "GOMART"
  | "LOMBOK";

export class OutletPoP {
  id = "";

  name = "";

  receiverId = "";

  active = false;

  gopayQRString: string | null = "";

  aspiQRString: string | null = "";

  type: "static" | "dynamic" = "static";

  terminalId = "";

  get isQRISPop(): boolean {
    return !!this.aspiQRString;
  }

  get isQrisEnabled(): boolean {
    return (
      this.active && this.gopayQRString !== null && this.aspiQRString !== null
    );
  }

  static fromJSON(json: IOutletPoP): OutletPoP {
    const pop = new OutletPoP();
    pop.id = json.pop_id;
    pop.name = json.name;
    pop.active = json.gopay.status === "active";
    pop.type = json.gopay.gopay_pop_type;
    pop.aspiQRString = json.gopay.aspi_qr_string || null;
    pop.gopayQRString = json.gopay.gopay_qr_string || null;
    pop.receiverId = json.gopay.gopay_receiver_id;
    pop.terminalId = json.gopay.aspi_terminal_id
      ? json.gopay.aspi_terminal_id.substring(4)
      : "";
    return pop;
  }
}

export class Merchant {
  @observable
  id = "";

  @observable
  idName = "";

  @observable
  idNumber = "";

  @observable
  idType = "";

  @observable
  idUrl = "";

  @observable
  createdAt?: moment.Moment;

  @observable
  name = "";

  @observable
  origin = "";

  @observable
  outletName = "";

  @observable
  outletAddress = "";

  @observable
  outletCity = "";

  @observable
  outletProvince = "";

  @observable
  outletPostalcode = "";

  @observable
  businessType: "individual" | "corporate" | string = "";

  @observable
  phone = "";

  @observable
  email = "";

  @observable
  featureTypes: string[] = [];

  @observable
  files: IOutletFiles[] = [];

  @observable
  merchantName = "";

  @observable
  merchantType = "";

  @observable
  npwp = "";

  @observable
  npwpName = "";

  @observable
  npwpUrl = "";

  @observable
  headquartersAddress = "";

  pops: OutletPoP[] = [];

  aspi?: IAspiJSON;

  bankAccountNumber = "";

  bankAccountName = "";

  bankName = "";

  bankCode = "";

  branchName = "";

  metadata: IMerchantJSON["metadata"] = {};

  @observable
  claimAdminStatus:
    | "submitted"
    | "follow_up"
    | "approved"
    | "rejected"
    | string = "";

  json = {} as IMerchantJSON;

  get isGoRestoActive(): boolean {
    const currentTime = moment();
    const statusIsNotSuspended =
      JSONTry(this.json, "applications.goresto.status") !== "suspended";
    const activateAt = moment(
      JSONTry(this.json, "applications.goresto.activate_at")
    );
    const deactivateAt = moment(
      JSONTry(this.json, "applications.goresto.deactivate_at")
    );
    const activationDateIsValid =
      activateAt.isBefore(currentTime) && deactivateAt.isAfter(currentTime);
    return statusIsNotSuspended && activationDateIsValid;
  }

  @computed
  get enabledProducts$(): ProductName[] {
    const products = {
      GORESTO: this.isGoRestoActive,
      GOPAY: JSONTry(this.json, "applications.gopay.status") === "active",
      POS: JSONTry<boolean>(this.json, "applications.pos.active") === "active",
      MIDTRANS: JSONTry(this.json, "applications.midtrans.onboard_at"),
      GOMART:
        JSONTry<boolean>(this.json, "applications.gomart.status") === "active",
      LOMBOK: JSONTry<boolean>(this.json, "applications.lombok.onboard_at"),
    };

    type Products = typeof products;

    return Object.keys(products).filter(
      (p) => products[p as keyof Products]
    ) as ProductName[];
  }

  @computed
  get productsLabel() {
    return this.enabledProducts$
      .sort((a, b) => a.localeCompare(b))
      .join("+")
      .toLowerCase();
  }

  static fromJSON = (json: IMerchantJSON, ignoreJSON = false) => {
    const merchant = new Merchant();
    if (!ignoreJSON) {
      merchant.json = json;
    }
    merchant.idNumber = json.id_number || "";
    merchant.idName = json.id_name || "";
    merchant.idUrl = json.id_url;
    merchant.name = json.merchant_name;
    merchant.origin = json.origin;
    merchant.outletName = json.outlet_name;
    merchant.outletAddress = json.outlet_address || "";
    merchant.outletCity = json.outlet_city;
    merchant.outletProvince = json.outlet_province;
    merchant.outletPostalcode = json.outlet_postal_code;
    merchant.businessType = json.business_type || "individual";
    merchant.bankAccountNumber = json.bank_account?.account_no || "";
    merchant.bankAccountName = json.bank_account?.account_name || "";
    merchant.bankName = json.bank_account?.bank_name || "";
    merchant.bankCode = json.bank_account?.bank_code || "";
    merchant.branchName = json.bank_account?.branch_name || "";
    merchant.phone = json.phone;
    merchant.email = json.email;
    merchant.metadata = json.metadata || {};
    merchant.aspi = json.aspi;
    merchant.pops = safeParse("array", json.pops).map(OutletPoP.fromJSON);
    merchant.idType =
      json.business_type === "individual"
        ? "KTP"
        : (json.id_type && json.id_type.toUpperCase()) || "SIUP";
    merchant.id = json.id;
    merchant.featureTypes = json.feature_types || [];
    merchant.files = json.files || [];
    merchant.metadata = json.metadata || {};
    merchant.merchantName = json.merchant_name;
    merchant.merchantType = json.merchant_type;
    merchant.headquartersAddress = json.headquarters_address || "";
    merchant.npwp = json.npwp || "";
    merchant.npwpName = json.npwp_name || "";
    merchant.npwpUrl = json.npwp_url || "";
    merchant.createdAt = moment(json.created_at);
    return merchant;
  };

  @computed
  get isCorporateMerchant(): boolean {
    return this.businessType === "corporate";
  }

  @computed
  get isEnterprise(): boolean {
    return this.merchantType === "enterprise";
  }

  @computed
  get isSmbKeyAccount(): boolean {
    return this.merchantType === "smb_key_accounts";
  }

  @computed
  get maskedBankAccountNumber(): string {
    return maskBankAccountNumber(this.bankAccountNumber);
  }

  @computed
  get maskedBankAccountName(): string {
    return maskBankAccountName(this.bankAccountName);
  }

  get bankAccountLast4Digits() {
    return (
      this.bankAccountNumber &&
      this.bankAccountNumber.slice(
        this.bankAccountNumber.length - 4,
        this.bankAccountNumber.length
      )
    );
  }

  get hasFeature() {
    const featureTypes = {
      MANUAL_PAYOUT: this.findFeatureTypes("manual_payout"),
      KRONOS: this.findFeatureTypes("kronos"),
      NEW_BILLING: this.findFeatureTypes("new_billing"),
      ONLINE_PAYOUT: this.findFeatureTypes("online_payout"),
      REFUND: this.findFeatureTypes("refund"),
      SNAP: this.findFeatureTypes("snap"),
      SPLIT_PAYOUT: this.findFeatureTypes("split_payout"),
    };

    return featureTypes;
  }

  findFeatureTypes(input: string): boolean {
    return this.featureTypes.some(
      (featureType) => featureType.toLowerCase() === input.toLowerCase()
    );
  }

  @computed
  get allowPassportOnboarding(): boolean {
    return (
      !this.enabledProducts$.includes("MIDTRANS") &&
      !this.json?.applications?.midtrans?.skip_express_onboarding
    );
  }
}
